import type React from "react";
import { useEffect, useState } from "react";
import {
  ActionIcon,
  Autocomplete,
  Box,
  Group,
  Loader,
  Popover,
  Select,
  Text,
} from "@mantine/core";
import { Button } from "@xplatform/design-system";
import { DatePickerInput } from "@mantine/dates";
import { IconX } from "@tabler/icons-react";
import dayjs from "dayjs";
import { useTranslation } from "react-i18next";
import { xplatformCoreApi } from "@services/xplatformCoreApi";
import { JobStatus, type JobListFilterViewModel } from "@models/job";
import type { IUserSearchResultViewModel } from "@models/user";
import "./JobFilterPanel.scss";

interface JobFilterPanelProps {
  anchorEl: HTMLElement | null;
  open: boolean;
  onClose: () => void;
  onApply: (filter: JobListFilterViewModel) => void;
  initialFilter: JobListFilterViewModel;
}

const MAX_DATE_RANGE_DAYS = 30;

const STATUS_OPTIONS: Array<JobStatus | "all"> = [
  "all",
  JobStatus.AwaitingUpload,
  JobStatus.Parsing,
  JobStatus.Running,
  JobStatus.Completed,
  JobStatus.Failed,
];

const userLabel = (o: IUserSearchResultViewModel): string => {
  const name = [o.firstName, o.lastName].filter(Boolean).join(" ");
  const email = o.email ?? "";
  return name ? `${email} (${name})` : email;
};

const JobFilterPanel: React.FC<JobFilterPanelProps> = ({
  anchorEl,
  open,
  onClose,
  onApply,
  initialFilter,
}) => {
  const { t } = useTranslation();

  const [startDate, setStartDate] = useState<Date | null>(
    initialFilter.startDate ? dayjs(initialFilter.startDate).toDate() : null,
  );
  const [endDate, setEndDate] = useState<Date | null>(
    initialFilter.endDate ? dayjs(initialFilter.endDate).toDate() : null,
  );
  const [selectedUser, setSelectedUser] =
    useState<IUserSearchResultViewModel | null>(null);
  const [userOptions, setUserOptions] = useState<IUserSearchResultViewModel[]>(
    [],
  );
  const [userSearchLoading, setUserSearchLoading] = useState(false);
  const [userInputValue, setUserInputValue] = useState("");
  const [status, setStatus] = useState<JobStatus | "all">(
    initialFilter.status ?? "all",
  );
  const [dateRangeError, setDateRangeError] = useState<string>("");

  useEffect(() => {
    if (!open) return;
    setStartDate(
      initialFilter.startDate ? dayjs(initialFilter.startDate).toDate() : null,
    );
    setEndDate(
      initialFilter.endDate ? dayjs(initialFilter.endDate).toDate() : null,
    );
    if (initialFilter.userId || initialFilter.userEmail) {
      setSelectedUser({
        externalId: initialFilter.userId,
        email: initialFilter.userEmail,
        firstName: null,
        lastName: null,
      });
      setUserInputValue(initialFilter.userEmail || initialFilter.userId || "");
    } else {
      setSelectedUser(null);
      setUserInputValue("");
    }
    setStatus(initialFilter.status ?? "all");
    setDateRangeError("");
  }, [open, initialFilter]);

  useEffect(() => {
    if (userInputValue.trim().length < 2) {
      setUserOptions([]);
      return;
    }
    const timer = setTimeout(async () => {
      setUserSearchLoading(true);
      try {
        setUserOptions(await xplatformCoreApi.searchUsers(userInputValue));
      } catch {
        setUserOptions([]);
      } finally {
        setUserSearchLoading(false);
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [userInputValue]);

  const validateDateRange = (start: Date | null, end: Date | null): boolean => {
    if (!start || !end) {
      setDateRangeError("");
      return true;
    }
    const diffDays = dayjs(end).diff(dayjs(start), "day");
    if (diffDays < 0) {
      setDateRangeError(t("mappingServicePage.jobFilterPanel.errors.invalidRange"));
      return false;
    }
    if (diffDays > MAX_DATE_RANGE_DAYS) {
      setDateRangeError(
        t("mappingServicePage.jobFilterPanel.errors.exceedsMaxRange", {
          days: MAX_DATE_RANGE_DAYS,
        }),
      );
      return false;
    }
    setDateRangeError("");
    return true;
  };

  const handleStartDateChange = (date: Date | null): void => {
    setStartDate(date);
    validateDateRange(date, endDate);
  };

  const handleEndDateChange = (date: Date | null): void => {
    setEndDate(date);
    validateDateRange(startDate, date);
  };

  const handleUserChange = (val: string): void => {
    setUserInputValue(val);
    const match = userOptions.find((o) => userLabel(o) === val);
    if (match) {
      setSelectedUser(match);
    } else {
      setSelectedUser(null);
    }
  };

  const handleApply = (): void => {
    if (!validateDateRange(startDate, endDate)) return;
    onApply({
      startDate: startDate ? dayjs(startDate).format("YYYY-MM-DD") : null,
      endDate: endDate ? dayjs(endDate).format("YYYY-MM-DD") : null,
      userId: selectedUser?.externalId ?? null,
      userEmail: selectedUser?.email ?? null,
      status,
    });
    onClose();
  };

  const handleClear = (): void => {
    setStartDate(null);
    setEndDate(null);
    setSelectedUser(null);
    setUserInputValue("");
    setStatus("all");
    setDateRangeError("");
  };

  return (
    <Popover opened={open} onClose={onClose} position="bottom-end" width={340}>
      <Popover.Target>
        <span
          ref={(el) => {
            if (el && anchorEl) {
              const rect = anchorEl.getBoundingClientRect();
              el.style.position = "fixed";
              el.style.top = `${rect.bottom}px`;
              el.style.left = `${rect.left}px`;
              el.style.width = "0";
              el.style.height = "0";
            }
          }}
        />
      </Popover.Target>

      <Popover.Dropdown className="job-filter-panel">
        <Box className="job-filter-panel__header">
          <Text size="sm" fw={600}>
            {t("mappingServicePage.jobFilterPanel.title")}
          </Text>
          <ActionIcon size="sm" variant="subtle" onClick={onClose} aria-label="Close">
            <IconX size={14} />
          </ActionIcon>
        </Box>

        <Box className="job-filter-panel__section">
          <Text size="xs" fw={600} mb={4}>
            {t("mappingServicePage.jobFilterPanel.dateRange.label")}
            <Text size="xs" c="dimmed" component="span" ml={4}>
              {t("mappingServicePage.jobFilterPanel.dateRange.maxRangeHint", {
                days: MAX_DATE_RANGE_DAYS,
              })}
            </Text>
          </Text>
          <Group grow gap="xs" align="flex-start">
            <DatePickerInput
              size="xs"
              label={t("mappingServicePage.jobFilterPanel.dateRange.startDate")}
              value={startDate}
              onChange={handleStartDateChange}
              valueFormat="DD/MM/YYYY"
              placeholder="DD/MM/YYYY"
              maxDate={endDate ?? new Date()}
              popoverProps={{ withinPortal: true, zIndex: 9999 }}
              clearable
            />
            <DatePickerInput
              size="xs"
              label={t("mappingServicePage.jobFilterPanel.dateRange.endDate")}
              value={endDate}
              onChange={handleEndDateChange}
              valueFormat="DD/MM/YYYY"
              placeholder="DD/MM/YYYY"
              minDate={startDate ?? undefined}
              maxDate={new Date()}
              popoverProps={{ withinPortal: true, zIndex: 9999 }}
              clearable
            />
          </Group>
          {dateRangeError && (
            <Text size="xs" c="red" mt={4}>
              {dateRangeError}
            </Text>
          )}
        </Box>

        <Box className="job-filter-panel__section">
          <Text size="xs" fw={600} mb={4}>
            {t("mappingServicePage.jobFilterPanel.createdBy.label")}
          </Text>
          <Autocomplete
            size="xs"
            data={userOptions.map(userLabel)}
            value={userInputValue}
            onChange={handleUserChange}
            filter={({ options }) => options}
            comboboxProps={{ withinPortal: true, zIndex: 9999 }}
            placeholder={t("mappingServicePage.jobFilterPanel.createdBy.placeholder")}
            rightSection={userSearchLoading ? <Loader size={14} /> : null}
          />
        </Box>

        <Box className="job-filter-panel__section">
          <Text size="xs" fw={600} mb={4}>
            {t("mappingServicePage.jobFilterPanel.status.label")}
          </Text>
          <Select
            size="xs"
            value={status}
            onChange={(val) => setStatus((val as JobStatus | "all") ?? "all")}
            data={STATUS_OPTIONS.map((s) => ({
              value: s,
              label: t(`mappingServicePage.jobFilters.${s}`, { defaultValue: s }),
            }))}
            allowDeselect={false}
            comboboxProps={{ withinPortal: true, zIndex: 9999 }}
          />
        </Box>

        <Group justify="flex-end" gap="xs" mt="sm">
          <Button size="xs" variant="default" onClick={handleClear}>
            {t("mappingServicePage.jobFilterPanel.buttons.clear")}
          </Button>
          <Button size="xs" onClick={handleApply} disabled={!!dateRangeError}>
            {t("mappingServicePage.jobFilterPanel.buttons.apply")}
          </Button>
        </Group>
      </Popover.Dropdown>
    </Popover>
  );
};

export default JobFilterPanel;
